import { useEffect, useMemo, useState } from "react";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { TabList } from "@/components/ui/legal/TabList";
import { Skeleton } from "@/components/ui/skeleton";
import {
  pageTypesConst,
  type ItemsType,
  type LegalType,
} from "@/types/entities/legal";
import { useTheme } from "@/hooks/use-theme";
// import type { LogsType } from "@/types/common/logs";
// import Logs from "@/components/common/log/logs";
import EditData from "./EditData";
import EditDataTerms from "./EditDataTerms";
import { useGetPrivacyPolicies, useGetTermsConditions } from "@/queries/legal";
import { useLegalStore } from "../../stores/legal/useLegalStore";
import NoData from "@/components/ui/no-data";
import { sanitizeHTML } from "@/utils/format";
import { cleanTitap } from "../../utils/format";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";

// const defaultActions: LogsType[] = [

//   {
//     title: "Hoy",
//     items: [
//       {
//         id: 1,
//         description: "Description of an action executed",
//         author: "Ricardo Guillen",
//         date: new Date("2019-02-02T19:28:00"),
//       },
//       {
//         id: 2,
//         description: "Description of an action executed",
//         author: "Ricardo Guillen",
//         date: new Date("2019-02-02T19:28:00"),
//       },
//       {
//         id: 3,
//         description: "Description of an action executed",
//         author: "Ricardo Guillen",
//         date: new Date("2019-02-02T19:28:00"),
//       },
//     ],
//   },
// ];

export default function LegalPage() {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions["privacy-policies"].edit);
  const [editMode, setEditMode] = useState(false);
  const {
    selected,
    terms,
    setTerms,
    policies,
    setPolicies,
    setSelected,
    setDefault,
    typeSelected,
    setType,
  } = useLegalStore();

  const { data: dataPolicies, isLoading: isLoadingPolicies } =
    useGetPrivacyPolicies({
      // filter: { active: true },
    });

  const { data: dataTerms, isLoading: isLoadingTerms } = useGetTermsConditions({
    // filter: { active: true },
  });

  // Cargar políticas de privacidad
  useEffect(() => {
    if (dataPolicies) {
      setPolicies(dataPolicies.data);
      // Solo actualizar selected si estamos en la pestaña de políticas
      if (typeSelected === pageTypesConst.policies) {
        const active = dataPolicies.data.find((val) => val.active);
        if (active) {
          setSelected({ ...active, content: sanitizeHTML(active.content) });
        } else setDefault();
      }
    }
  }, [dataPolicies, setPolicies, typeSelected, setDefault, setSelected]);

  // Cargar términos y condiciones
  useEffect(() => {
    if (dataTerms) {
      setTerms(dataTerms.data);
      // Solo actualizar selected si estamos en la pestaña de términos
      if (typeSelected === pageTypesConst.terms) {
        const active = dataTerms.data.find((val) => val.active);
        if (active) {
          // Use display_content if available (from applicant-conditions endpoint)
          const contentToUse = active.display_content || active.content;
          setSelected({ ...active, content: sanitizeHTML(contentToUse) });
        } else setDefault();
      }
    }
  }, [dataTerms, setTerms, typeSelected, setDefault, setSelected]);

  const items: ItemsType[] = useMemo(
    () => [
      {
        label: "Política de privacidad",
        onClick: () => {
          setType(pageTypesConst.policies);

          const active = policies.find((val) => val.active);
          if (active) {
            setSelected({
              ...active,
              content: sanitizeHTML(active.content),
            });
          } else setDefault();
        },
        isSelected: typeSelected === pageTypesConst.policies,
        isDisabled: false,
      },
      {
        label: "Términos y condiciones",
        onClick: () => {
          setType(pageTypesConst.terms);
          const active = terms.find((val) => val.active);
          if (active) {
            // Use display_content if available (from applicant-conditions endpoint)
            const contentToUse = active.display_content || active.content;
            setSelected({ ...active, content: sanitizeHTML(contentToUse) });
          } else setDefault();
        },
        isSelected: typeSelected === pageTypesConst.terms,
        isDisabled: false,
      },
    ],
    [setType, typeSelected, policies, terms, setDefault, setSelected],
  );

  const handleReset = () => {
    if (typeSelected === pageTypesConst.policies) {
      findLegal(policies);
    } else findLegal(terms);
    setEditMode(false);
  };

  const findLegal = (data: LegalType[]) => {
    const active = data.find((val) => val.active);
    if (active) {
      setSelected({ ...active, content: cleanTitap(active.content) });
    } else setDefault();
  };

  const isLoading =
    typeSelected === pageTypesConst.policies
      ? isLoadingPolicies
      : isLoadingTerms;

  return (
    <div className="flex flex-col w-full gap-8">
      <div className="flex flex-row w-full items-start gap-6">
        <div className="flex flex-col flex-grow min-w-0">
          <div className="flex flex-col laptop:flex-row gap-4 flex-grow">
            <TabList items={items} isDisabled={editMode} />
            {!editMode && selected?.content && canEdit && (
              <Button
                variant="secondary"
                className="hover:cursor-pointer whitespace-nowrap"
                onClick={() => setEditMode(true)}
              >
                <span className="font-semibold text-action text-base flex-shrink-0">
                  Editar contenido
                </span>
                <Icon type="fas" name="pen" className="text-current" />
              </Button>
            )}
          </div>
          {!editMode && (
            <ActiveData
              data={selected?.content || ""}
              isLoading={isLoading}
              onClick={() => setEditMode(true)}
              typeSelected={typeSelected}
            />
          )}
        </div>
        {/* {!editMode && <HistoryData isLoading={isLoading} />} */}
      </div>
      {editMode && typeSelected === pageTypesConst.policies && (
        <EditData onClose={handleReset} />
      )}
      {editMode && typeSelected === pageTypesConst.terms && (
        <EditDataTerms onClose={handleReset} />
      )}
    </div>
  );
}

const ActiveData = ({
  data,
  isLoading,
  onClick,
  typeSelected,
}: {
  data: string;
  isLoading: boolean;
  onClick: () => void;
  typeSelected: string;
}) => {
  const { hasPermission } = useAuthorization();
  const { theme } = useTheme();
  const canCreate = hasPermission(Permissions["privacy-policies"].create);

  const titles = {
    policies: {
      noData: "No has creado una política de privacidad",
      description:
        "La plataforma requiere una política clara sobre el tratamiento de datos personales.",
      button: "Crear política",
    },
    terms: {
      noData: "No has creado términos y condiciones",
      description:
        "La plataforma requiere términos y condiciones claros para los usuarios.",
      button: "Crear términos",
    },
  };

  const currentType =
    typeSelected === pageTypesConst.policies ? "policies" : "terms";

  if (isLoading)
    return (
      <div className="flex flex-col gap-4" data-testid="page-skeletor">
        {[1, 2].map((item) => (
          <div
            key={item}
            className="mt-14 space-y-2 flex-grow max-w-none xl:px-36"
          >
            <Skeleton className="h-8 w-[250px] mb-10" />
            {[1, 2, 3, 4].map((item) => (
              <Skeleton
                key={item}
                className="h-4 w-[500px] laptop:w-[750px] xl:w-[800px]"
              />
            ))}
          </div>
        ))}
      </div>
    );

  if (!isLoading && data === "") {
    return (
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-300px)]">
        <NoData
          title={titles[currentType].noData}
          description={titles[currentType].description}
        >
          {canCreate && (
            <Button
              className="cursor-pointer py-1.5 px-2 rounded-lg gap-2.5"
              onClick={onClick}
            >
              <span>{titles[currentType].button}</span>
              <Icon type="fas" name="plus" className="text-current" />
            </Button>
          )}
        </NoData>
      </div>
    );
  }
  return (
    <div className="flex-grow min-w-0 xl:px-36" data-testid="page-data">
      <div
        className={`mt-14 w-full flex-grow prose max-w-none ${theme === "dark" ? "prose-invert" : ""}`}
        dangerouslySetInnerHTML={{
          __html: data,
        }}
      ></div>
    </div>
  );
};

// const HistoryData = ({ isLoading }: { isLoading: boolean }) => {
//   if (isLoading)
//     return (
//       <Skeleton className="hidden laptop:block flex-shrink-0 rounded-lg p-4 mt-14 w-[350px] h-[320px]" />
//     );
//   return (
//     <div className="hidden laptop:block w-[320px] flex-shrink-0 rounded-lg p-4 bg-neutral-object">
//       {defaultActions.map((item) => (
//         <Logs key={item.title} title={item.title} items={item.items} />
//       ))}
//     </div>
//   );
// };
